import { QueryKeys } from "@/constants/Query";
import type { WaitingRoomCountResponse } from "@/features/LiveQueue/network/useWaitingRoomCount";
import {
  type WaitingRoomItem,
  type WaitingRoomResponse,
  WaitingRoomType,
} from "@/features/LiveQueue/network/useWaitingRoomDashboard";
import { useUser } from "@/network/useUser";
import { socketsAtom } from "@/store/socketsAtom";
import { Practitioner } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useCallback, useEffect } from "react";

// The count endpoint shares the dashboard prefix and is keyed with the literal
// "count" segment. Use it to skip the count cache when iterating list caches.
const isCountKey = (key: readonly unknown[]) => key.includes("count");

// Dashboard list caches are keyed via LiveQueueDashboard.WITH_PARAMS, which
// appends a { mineOnly, abridged } params object as the last segment.
const isMineOnlyVariantKey = (key: readonly unknown[]) => {
  const last = key[key.length - 1];
  return (
    typeof last === "object" &&
    last !== null &&
    "mineOnly" in last &&
    (last as { mineOnly?: boolean }).mineOnly === true
  );
};

export function useLiveQueueSocketEvents() {
  const socket = useAtomValue(socketsAtom)?.distribution;
  const queryClient = useQueryClient();
  const { data: user } = useUser();
  const currentUserId = user?.userId;

  // Bump or decrement the global Live nav badge count without refetching.
  const adjustWaitingRoomCount = useCallback(
    (delta: 1 | -1) => {
      queryClient.setQueryData<WaitingRoomCountResponse>(
        QueryKeys.Provider.LiveQueueDashboard.COUNT,
        (prev) => {
          if (!prev) return prev;
          return {
            waitingRoomCount: Math.max(prev.waitingRoomCount + delta, 0),
          };
        },
      );
    },
    [queryClient],
  );

  const addPatientToWaitingRoom = useCallback(
    (patient: WaitingRoomItem) => {
      const isAssignedToCurrentUser =
        patient.encounter.assigneeProfessionalUserId === currentUserId;

      const entries = queryClient.getQueriesData<WaitingRoomResponse>({
        queryKey: QueryKeys.Provider.LiveQueueDashboard.DEFAULT,
      });

      // Duplicate detection: if any list cache already contains this encounter
      // we've already incremented the count for it. We still attempt to patch
      // each cache below — in case a sibling cache hydrated separately and
      // doesn't have it yet — but skip the count bump.
      const isDuplicate = entries.some(
        ([queryKey, prev]) =>
          !isCountKey(queryKey) &&
          prev?.patients.some(
            ({ encounter }) => encounter.id === patient.encounter.id,
          ),
      );

      for (const [queryKey, prev] of entries) {
        if (isCountKey(queryKey) || !prev) continue;
        // mineOnly caches only show the current user's visits; skip adds that
        // aren't assigned to them (but still count globally).
        if (isMineOnlyVariantKey(queryKey) && !isAssignedToCurrentUser) {
          continue;
        }
        const alreadyPresent = prev.patients.some(
          ({ encounter }) => encounter.id === patient.encounter.id,
        );
        if (alreadyPresent) continue;
        queryClient.setQueryData<WaitingRoomResponse>(queryKey, {
          ...prev,
          patients: [...prev.patients, patient],
        });
      }

      if (!isDuplicate) {
        adjustWaitingRoomCount(1);
      }
    },
    [queryClient, currentUserId, adjustWaitingRoomCount],
  );

  const removePatientFromWaitingRoom = useCallback(
    (id: string) => {
      const entries = queryClient.getQueriesData<WaitingRoomResponse>({
        queryKey: QueryKeys.Provider.LiveQueueDashboard.DEFAULT,
      });

      let changedAnyCache = false;
      for (const [queryKey, prev] of entries) {
        if (isCountKey(queryKey) || !prev) continue;
        const next = prev.patients.filter(
          (patient) => patient.encounter.id !== id,
        );
        if (next.length === prev.patients.length) continue;
        queryClient.setQueryData<WaitingRoomResponse>(queryKey, {
          ...prev,
          patients: next,
        });
        changedAnyCache = true;
      }

      // Only decrement when this event actually removed the patient from a
      // cache — same rationale as the add path.
      if (changedAnyCache) {
        adjustWaitingRoomCount(-1);
      }
    },
    [queryClient, adjustWaitingRoomCount],
  );

  useEffect(() => {
    // Only attach listeners if user data is available. Handlers can be attached
    // prior to the socket connecting.
    if (!user || !socket) return;

    const practitioner = new Practitioner(user.professional.fhir || {});

    // Rebalance events are infrequent and reorder the whole room; full refetch
    // is the only reliable way to pick up the new ordering.
    const handleWaitingRoomRebalance = () => {
      queryClient.invalidateQueries({
        queryKey: QueryKeys.Provider.LiveQueueDashboard.DEFAULT,
      });
    };

    const handleWaitingRoomRemove = (message: { id: string }) => {
      removePatientFromWaitingRoom(message.id);
    };

    const handleWaitingRoomAdd = (patient: WaitingRoomItem) => {
      const { roomType } = patient.encounter;
      const isAssignedToUser =
        patient.encounter.assigneeProfessionalUserId === user.userId;

      if (roomType === WaitingRoomType.CA && practitioner.isCa) {
        if (isAssignedToUser) {
          // Handed off *back* to the CA queue by this user — remove from view.
          removePatientFromWaitingRoom(patient.encounter.id);
        } else {
          addPatientToWaitingRoom(patient);
        }
      } else if (
        roomType === WaitingRoomType.CLINICIAN &&
        practitioner.isLicensedClinician
      ) {
        addPatientToWaitingRoom(patient);
      }
      // If user is neither CA nor Clinician, or roomType doesn't match, do nothing
    };

    socket.on("waiting_room_dashboard_add", handleWaitingRoomAdd);
    socket.on("waiting_room_dashboard_remove", handleWaitingRoomRemove);
    socket.on("waiting_room_dashboard_rebalance", handleWaitingRoomRebalance);

    return () => {
      socket.off("waiting_room_dashboard_add", handleWaitingRoomAdd);
      socket.off("waiting_room_dashboard_remove", handleWaitingRoomRemove);
      socket.off(
        "waiting_room_dashboard_rebalance",
        handleWaitingRoomRebalance,
      );
    };
  }, [
    socket,
    queryClient,
    user,
    addPatientToWaitingRoom,
    removePatientFromWaitingRoom,
  ]);

  // This hook performs side effects only, doesn't need to return anything
  return null;
}
